import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CARD_IMAGE_WIDTHS, optimizedImageSrcSet, optimizedImageUrl } from '../../utils/imageUrl';

// Card widths: 280px fixed on mobile, 2/3/4 columns on md/lg/xl.
const CARD_IMAGE_SIZES = '(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 768px) 50vw, 280px';

// Autoplay previews are skipped for data-saver / reduced-motion users (hover still plays).
const allowAutoPreview = () => {
  if (typeof window === 'undefined') return false;
  if (navigator.connection?.saveData) return false;
  return !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
};

// Resolves once the window 'load' event has fired, so card videos never compete
// with the page's critical images/scripts.
let pageLoadPromise;
const whenPageLoaded = () => {
  if (!pageLoadPromise) {
    pageLoadPromise = document.readyState === 'complete'
      ? Promise.resolve()
      : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
  }
  return pageLoadPromise;
};

// Reusable LandCard component used in FeaturedLands and LikedLandsPage
// Props:
// - listing: property object
// - onClick: open details
// - liked: boolean
// - onToggleLike: function to toggle like
// - showPrice: whether to show price (default true). Homepage hides it.
// - showStatus: whether to show status badge (default false). Hidden everywhere now.
// - priority: load the image eagerly with high priority (above-the-fold cards only)
const LandCard = ({ listing, onClick, liked = false, onToggleLike, showPrice = true, showStatus = false, priority = false }) => {
  const { t } = useLanguage();
  const defaultImage = "https://plus.unsplash.com/premium_photo-1669227514138-d5e43441b5e7?q=70&w=800&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasActivatedVideo, setHasActivatedVideo] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isImageReady, setIsImageReady] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [useOriginalImage, setUseOriginalImage] = useState(false);
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const midStartRef = useRef(null);
  const midEndRef = useRef(null);
  const isHoveredRef = useRef(false);
  const isInViewRef = useRef(false);

  // ─── Resolve helpers ───────────────────────────────────────────────────────

  const resolvePreviewImage = useCallback(() => {
    const imgs = listing?.images;
    if (Array.isArray(imgs) && imgs.length > 0) {
      const first = imgs[0];
      if (typeof first === 'string' && first) return first;
      if (first && typeof first === 'object') return first.url || first.src || first.path || defaultImage;
    }
    if (typeof imgs === 'string' && imgs) return imgs;
    if (listing?.coverImage) return listing.coverImage;
    if (listing?.thumbnail) return listing.thumbnail;
    if (listing?.image) return listing.image;
    if (listing?.previewImage) return listing.previewImage;
    if (listing?.previewImageUrl) return listing.previewImageUrl;
    if (listing?.photoUrl) return listing.photoUrl;
    if (listing?.photo) return listing.photo;
    const media = listing?.media;
    if (Array.isArray(media) && media.length > 0) {
      const m0 = media[0];
      if (typeof m0 === 'string' && m0) return m0;
      if (m0 && typeof m0 === 'object') return m0.url || m0.src || m0.path || defaultImage;
    }
    return defaultImage;
  }, [listing]);

  const resolveTitle = useCallback(() =>
    listing?.title || listing?.name || listing?.layoutName || listing?.layout_name || listing?.propertyTitle || listing?.property_title || 'Land Listing',
    [listing]);

  const resolveLocation = useCallback(() =>
    listing?.location || listing?.city || listing?.district || listing?.place || listing?.location_name || '',
    [listing]);

  const resolveLastVideoUrl = useCallback(() => {
    const vids = listing?.videos;
    if (!Array.isArray(vids) || vids.length === 0) return null;
    const last = vids[vids.length - 1];
    if (typeof last === 'string' && last) return last;
    if (last && typeof last === 'object') return last.url || last.src || last.path || null;
    return null;
  }, [listing]);

  const lastVideoUrl = resolveLastVideoUrl();
  const previewImage = resolvePreviewImage();

  // ─── Video behaviour ───────────────────────────────────────────────────────

  // Track viewport visibility (works on touch devices too) so off-screen videos
  // are never downloaded and are paused when scrolled away.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !lastVideoUrl) return;
    if (typeof IntersectionObserver === 'undefined') {
      isInViewRef.current = true;
      setIsInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      isInViewRef.current = entry.isIntersecting;
      setIsInView(entry.isIntersecting);
    }, { rootMargin: '150px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [lastVideoUrl]);

  useEffect(() => {
    if (!lastVideoUrl) return;
    let cancelled = false;
    whenPageLoaded().then(() => { if (!cancelled) setIsPageLoaded(true); });
    return () => { cancelled = true; };
  }, [lastVideoUrl]);

  // Auto-mount the preview only after the page has loaded and this card's photo is
  // showing, so the photo appears first and the video takes over once it can play.
  useEffect(() => {
    if (lastVideoUrl && isInView && isImageReady && isPageLoaded && allowAutoPreview()) {
      setHasActivatedVideo(true);
    }
  }, [lastVideoUrl, isInView, isImageReady, isPageLoaded]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isInView || isHoveredRef.current) {
      if (video.paused && midStartRef.current !== null) video.play().catch(() => {});
    } else if (!video.paused) {
      video.pause();
    }
  }, [isInView]);

  // Called once the video metadata (duration) is known
  const handleMetadataLoaded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const duration = video.duration;
    if (!duration || !isFinite(duration)) return;

    // Calculate the 3-second window centred on the middle
    const CLIP = 3;
    const center = duration / 2;
    const start = Math.max(0, center - CLIP / 2);
    const end = Math.min(duration, start + CLIP);
    midStartRef.current = start;
    midEndRef.current = end;

    if (isHoveredRef.current) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      // Seek to the start of the preview clip and play it if the card is visible
      video.currentTime = start;
      if (isInViewRef.current) video.play().catch(() => {});
    }
  }, []);

  // Called on every timeupdate — enforces the 3-second loop when not hovered
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || isHoveredRef.current) return;
    if (midEndRef.current !== null && video.currentTime >= midEndRef.current) {
      video.currentTime = midStartRef.current;
    }
  }, []);

  useEffect(() => {
    isHoveredRef.current = isHovered;
    const video = videoRef.current;
    if (!video) return;

    if (isHovered) {
      // Full video: seek to beginning and play
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      // Back to 3s middle preview loop
      if (midStartRef.current !== null) {
        video.currentTime = midStartRef.current;
        video.play().catch(() => {});
      }
      // if metadata not yet loaded, handleMetadataLoaded will start the 3s loop
    }
  }, [isHovered]);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={cardRef}
      className="bg-white rounded-lg overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-shadow relative"
      onClick={() => { setIsLoading(true); onClick(); }}
      onMouseEnter={() => { setHasActivatedVideo(true); setIsHovered(true); }}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="aspect-[4/5] relative overflow-hidden">

        {/* ── Image stays in normal flow to hold container height ── */}
        <img
          src={useOriginalImage ? previewImage : optimizedImageUrl(previewImage, CARD_IMAGE_WIDTHS[1])}
          srcSet={useOriginalImage ? undefined : optimizedImageSrcSet(previewImage)}
          sizes={CARD_IMAGE_SIZES}
          alt={listing?.title ? `${listing.title} preview image` : 'Land Listing preview image'}
          className="w-full h-full object-cover"
          loading={priority ? 'eager' : 'lazy'}
          fetchpriority={priority ? 'high' : undefined}
          decoding="async"
          referrerPolicy="no-referrer"
          onLoad={() => setIsImageReady(true)}
          onError={(e) => {
            // Optimized copy failed → try the original upload, then the stock fallback.
            if (!useOriginalImage && optimizedImageSrcSet(previewImage)) {
              setUseOriginalImage(true);
            } else if (e.currentTarget.src !== defaultImage) {
              e.currentTarget.removeAttribute('srcset');
              e.currentTarget.src = defaultImage;
            } else {
              setIsImageReady(true);
            }
          }}
        />

        {/* Video mounts once the card nears the viewport (or on hover), then plays the
             3s middle clip by default and the full video on hover. */}
        {lastVideoUrl && hasActivatedVideo && (
          <video
            ref={videoRef}
            src={lastVideoUrl}
            className={`absolute inset-0 w-full h-full object-cover z-10 transition-opacity duration-500 ${isVideoPlaying ? 'opacity-100' : 'opacity-0'}`}
            style={{ transform: 'scale(1.15)', transformOrigin: 'center' }}
            onPlaying={() => setIsVideoPlaying(true)}
            muted
            playsInline
            loop={isHovered}
            preload="metadata"
            onLoadedMetadata={handleMetadataLoaded}
            onTimeUpdate={handleTimeUpdate}
          />
        )}

        {/* Land type capsule - top left */}
        {(listing?.landType || listing?.type) && (
          <div className="absolute top-3 left-3 z-20">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm">
              {listing.landType
                ? listing.landType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                : listing.type}
            </span>
          </div>
        )}

        {/* Aerial update info with gradient overlay */}
        <div className="absolute bottom-10 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 py-1 z-20">
          {String(listing?.id) === 'dbf1e4bd-b2e0-450f-82f2-7aedca2744f3' && listing?.lastAerialUpdate ? (
            <p className="text-white text-xs font-medium opacity-90 drop-shadow-lg">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-brand-blue text-white">Monitored</span>  Arial updated: {listing.lastAerialUpdate}
            </p>
          ) : (
            <p className="text-white text-xs font-medium opacity-90 drop-shadow-lg">
              {listing?.lastAerialUpdate ? (
                <>Last updated aerial view: {listing.lastAerialUpdate}</>
              ) : (
                <>Aerial view not available</>
              )}
            </p>
          )}
        </div>

        {/* Instagram-style CTA button - positioned at very bottom */}
        <button
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className="absolute bottom-0 left-0 right-0 w-full bg-brand-green text-white font-semibold text-sm py-2.5 flex items-center justify-center z-20"
        >
          <span>{t('common.viewThisLand')}</span>
          <svg className="w-4 h-4 absolute right-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* ── Card info ── */}
      <div className="p-4 font-montserrat">
        <div className="flex items-center justify-between mb-3 font-montserrat">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-brand-blue">
              {resolveTitle()}
            </span>
            {showStatus && (
              <span className="px-2 py-1 bg-brand-green text-white text-sm rounded">
                {listing?.status || 'Available'}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {/* Like icon */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleLike && onToggleLike(); }}
              className="p-0 bg-transparent border-0 focus:outline-none"
              aria-label={liked ? 'Unlike' : 'Like'}
              title={liked ? 'Unlike' : 'Like'}
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="43.259" height="38.677" viewBox="0 0 43.259 38.677">
                <g transform="translate(1.5 1.5)">
                  <path d="M20.128,35.677a2.351,2.351,0,0,1-1.73-.736L5.166,21.849a6.806,6.806,0,0,1-.606-.586c-.262-.281-.681-.777-1.248-1.511A19.144,19.144,0,0,1,1.787,17.5,13.951,13.951,0,0,1,.55,14.652,10.928,10.928,0,0,1,0,11.307,11.244,11.244,0,0,1,3.024,3.029,11.153,11.153,0,0,1,11.259,0a9.419,9.419,0,0,1,3.048.529,11.641,11.641,0,0,1,2.807,1.389c.819.551,1.527,1.075,2.113,1.561.31.241.605.512.9.782.293-.27.6-.541.9-.782.591-.486,1.292-1.01,2.112-1.561A11.582,11.582,0,0,1,25.952.529,9.415,9.415,0,0,1,29,0a11.17,11.17,0,0,1,8.24,3.029,11.254,11.254,0,0,1,3.019,8.278c0,3.493-1.74,7.053-5.176,10.571L21.849,34.96a2.381,2.381,0,0,1-1.721.717"
                    fill={liked ? '#39516c' : 'none'} stroke="#39516c" strokeWidth="3" />
                </g>
              </svg>
            </button>
            {/* Share button */}
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const shareData = {
                    title: listing?.title || 'Land Listing',
                    text: listing?.title || 'Check out this land listing',
                    url: `${window.location.origin}/land-details/${listing?.id}`,
                  };
                  if (navigator.share) {
                    await navigator.share(shareData);
                  } else {
                    await navigator.clipboard.writeText(shareData.url);
                    alert('Link copied to clipboard!');
                  }
                } catch (err) {
                  if (err.name !== 'AbortError') console.error('Share failed:', err);
                }
              }}
              className="p-0 bg-transparent border-0 focus:outline-none"
              aria-label="Share"
              title="Share"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="47.746" height="38.812" viewBox="0 0 47.746 38.812">
                <path id="share" d="M46.033,17.851,31.288,4.447c-1.117-1.117-2.457,0-2.457,1.787v6.7A26.637,26.637,0,0,0,5.15,28.128a34.191,34.191,0,0,0-3.128,9.159c-.447,2.234,2.9,3.351,4.245,1.34C11.182,30.808,19.448,25.67,28.831,25.67v7.372c0,1.787,1.34,2.9,2.457,1.787l14.744-13.4A2.718,2.718,0,0,0,46.033,17.851Z" transform="translate(-0.457 -2.42)" fill="none" stroke="#39516c" strokeWidth="3" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex items-center text-gray-600 mb-2">
          <svg className="w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" width="20.1" height="26.78" viewBox="0 0 20.1 26.78">
            <path d="M15,1.9A10.089,10.089,0,0,0,4.96,12.02c0,7.93,9.1,16.11,9.49,16.45a.852.852,0,0,0,1.12,0c.39-.34,9.49-8.53,9.49-16.45A10.1,10.1,0,0,0,15.02,1.9h0Zm0,15.62a5.58,5.58,0,1,1,5.58-5.58A5.582,5.582,0,0,1,15,17.52Z" transform="translate(-4.96 -1.9)" fill="#12793c" />
          </svg>
          <span>{resolveLocation() || 'Location'}</span>
          {listing?.area && (
            <>
              <span className="mx-2">•</span>
              <span>{listing.area} acres</span>
            </>
          )}
        </div>
        {showPrice && listing?.price && (
          <p className="text-brand-green font-semibold mt-2">₹{listing.price}</p>
        )}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-brand-green"></div>
            <p className="text-brand-green font-semibold text-sm">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandCard;
